/**
 * @file Sources de bonus standard (upgrades, rebirth, skins, buffs, etoiles, reliques, familiers, poissons) -> modificateurs.
 */
import { Decimal } from '@shared/bignum';
import { UPGRADE_IDS } from '@shared/enums';
import type { GameConfig } from '@config/index';
import type { ModifierDef } from '@config/types';
import { rebirthMultiplier, starMultiplier, upgradeMultiplier } from '../balance/formulas';
import type { PlayerData } from '../state/PlayerData';
import type { Modifier, ModifierSink, ModifierSource } from './Modifier';

const fromDef = (source: string, d: ModifierDef): Modifier => ({
  source,
  stat: d.stat,
  op: d.op,
  value: Decimal.fromValue(d.value),
  filter: d.filter,
});

/** Multiplie un ModifierDef "par niveau" : mult -> value^niveau, add -> value*niveau. */
const scaled = (source: string, d: ModifierDef, level: number): Modifier => ({
  source,
  stat: d.stat,
  op: d.op,
  value: d.op === 'mult' ? Decimal.pow(Decimal.fromValue(d.value), level) : Decimal.fromValue(d.value).times(level),
  filter: d.filter,
});

/** Power / Speed / Money (run courante). */
export const upgradeSource = (cfg: GameConfig, getData: () => PlayerData): ModifierSource => ({
  id: 'upgrades',
  collect(sink: ModifierSink) {
    const data = getData();
    for (const id of UPGRADE_IDS) {
      const spec = cfg.balance.upgrades.specs[id];
      sink.push({ source: `upgrade:${id}`, stat: spec.stat, op: 'mult', value: upgradeMultiplier(spec, data.run.upgrades[id]) });
    }
  },
});

/** Bonus permanents de Rebirth : x1.05^niveau sur degats, cadence, argent. */
export const rebirthSource = (cfg: GameConfig, getData: () => PlayerData): ModifierSource => ({
  id: 'rebirth',
  collect(sink) {
    const lv = getData().rebirth.levels;
    sink.push({ source: 'rebirth:damage', stat: 'damage', op: 'mult', value: rebirthMultiplier(cfg.balance, lv.damage) });
    sink.push({ source: 'rebirth:attackSpeed', stat: 'attackSpeed', op: 'mult', value: rebirthMultiplier(cfg.balance, lv.attackSpeed) });
    sink.push({ source: 'rebirth:money', stat: 'money', op: 'mult', value: rebirthMultiplier(cfg.balance, lv.money) });
  },
});

/** Skins possedes : bonus actifs meme non equipes (comme l'original). */
export const skinSource = (cfg: GameConfig, getData: () => PlayerData): ModifierSource => {
  const byId = new Map(cfg.skins.map((s) => [s.id, s]));
  return {
    id: 'skins',
    collect(sink) {
      for (const id of getData().skins.owned) {
        const s = byId.get(id);
        if (s) for (const b of s.bonus) sink.push(fromDef(`skin:${id}`, b));
      }
    },
  };
};

/** Buffs temporaires actifs (x3, x4 en super). */
export const buffSource = (cfg: GameConfig, getData: () => PlayerData): ModifierSource => ({
  id: 'buffs',
  collect(sink) {
    const buffs = getData().buffs;
    for (const spec of cfg.balance.buffs) {
      const st = buffs[spec.id];
      if (st.seconds > 0) sink.push({ source: `buff:${spec.id}`, stat: spec.stat, op: 'mult', value: new Decimal(st.superActive ? spec.superMult : spec.mult) });
    }
  },
});

/** Etoiles : multiplicateur de degats propre a chaque slot d'equipement. */
export const starSource = (cfg: GameConfig, getData: () => PlayerData): ModifierSource => ({
  id: 'stars',
  collect(sink) {
    getData().stars.levels.forEach((level, slot) => {
      if (level > 0) sink.push({ source: `star:${slot}`, stat: 'damage', op: 'mult', value: starMultiplier(cfg.balance, level), filter: { slots: [slot] } });
    });
  },
});

/** Reliques (squelette) : modificateur par niveau. */
export const relicSource = (cfg: GameConfig, getData: () => PlayerData): ModifierSource => ({
  id: 'relics',
  collect(sink) {
    const levels = getData().relics.levels;
    for (const r of cfg.relics) {
      const lv = levels[r.id] ?? 0;
      if (lv > 0) sink.push(scaled(`relic:${r.id}`, r.perLevel, lv));
    }
  },
});

/** Familiers equipes + poissons possedes (squelettes) : bonus plats. */
export const companionSource = (cfg: GameConfig, getData: () => PlayerData): ModifierSource => {
  const pets = new Map(cfg.pets.map((p) => [p.id, p]));
  const fish = new Map(cfg.fish.map((f) => [f.id, f]));
  return {
    id: 'companions',
    collect(sink) {
      const d = getData();
      for (const id of d.pets.equipped) for (const b of pets.get(id)?.bonus ?? []) sink.push(fromDef(`pet:${id}`, b));
      for (const id of d.fish.owned) for (const b of fish.get(id)?.bonus ?? []) sink.push(fromDef(`fish:${id}`, b));
    },
  };
};

/** Toutes les sources standard, dans un ordre stable. */
export const standardSources = (cfg: GameConfig, getData: () => PlayerData): ModifierSource[] => [
  upgradeSource(cfg, getData),
  rebirthSource(cfg, getData),
  skinSource(cfg, getData),
  buffSource(cfg, getData),
  starSource(cfg, getData),
  relicSource(cfg, getData),
  companionSource(cfg, getData),
];
